'use client';

import { useEffect, useRef, useState } from 'react';
import type { LiveHistoryRun, LiveSite } from '@content-removal/shared';
import { GoogleIcon, LockIcon } from '../icons';

const STEP_LABEL: Record<LiveSite['step'], { text: string; tone: string }> = {
  found: { text: 'Found', tone: 'chip-danger' },
  processing: { text: 'Processing', tone: 'chip-progress' },
  delisting: { text: 'Delisting requested', tone: 'chip-progress' },
  removed: { text: 'Removed', tone: 'chip-resolved' },
};

/** How sure we are it is hers, as a ring read before the name. */
export function Ring({ site, mini = false }: { site?: LiveSite; mini?: boolean }) {
  const match = site?.match ?? 96;
  return (
    <span
      className={`ov-ring${site?.hot ? ' is-hot' : ''}${mini ? ' is-mini' : ''}`}
      style={{ '--p': match } as React.CSSProperties}
      title={site ? `We are ${match}% sure this is your content` : undefined}
    >
      <span>{mini ? '' : site?.match == null ? '?' : `${site.match}%`}</span>
    </span>
  );
}

function when(iso: string): string {
  const date = new Date(iso);
  const today = new Date().toDateString() === date.toDateString();
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return today ? `today ${time}` : `${date.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${time}`;
}

function flag(site: LiveSite): string {
  if (site.impersonation) return 'Someone using your name';
  return site.inGoogle ? `Urgent, ${site.inGoogle} in Google results` : 'Not in Google results';
}

/** One site: the row, and the pages under it when it is open. */
function Case({ site, open, flash, onToggle }: { site: LiveSite; open: boolean; flash: boolean; onToggle: () => void }) {
  const step = STEP_LABEL[site.step];
  return (
    <div className={`ov-case${open ? ' is-open' : ''}${flash ? ' is-flash' : ''}`} data-site={site.domain}>
      <button type="button" className="ov-case-row" aria-expanded={open} onClick={onToggle}>
        <Ring site={site} />
        <span className="ov-dom">
          <b>{site.domain}</b>
          <span className={site.hot ? 'is-hot' : ''}>{flag(site)}</span>
        </span>
        <span className="ov-num">
          {site.linkCount}
          <small>{site.linkCount === 1 ? 'link' : 'links'}</small>
        </span>
        <span className="ov-when">{when(site.firstSeen)}</span>
        <span><span className={`chip ${step.tone}`}>{step.text}</span></span>
        <svg className="ov-chev" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      <div className="ov-links">
        {site.links.map((link) => (
          <div className="ov-link" key={link.id}>
            <span className="ov-url" title={link.url}>{link.url}</span>
            <span className={`chip ${link.online ? 'chip-danger ov-st-online' : 'chip-resolved ov-st-gone'}`}>{link.online ? 'Online' : 'Gone'}</span>
            <span className={`chip ${link.cameBack ? 'chip-attention' : 'chip-progress'}`}>{link.cameBack ? 'Came back' : 'New'}</span>
            <span>
              {link.inGoogle ? <span className="ov-g">In <GoogleIcon />Google</span> : link.removedFromGoogle ? <span className="ov-g is-out">Removed from <GoogleIcon />Google</span> : null}
            </span>
            {/* Straight to a new tab, and the site is not told where she came from:
                no referrer means no trace of our address in its logs. */}
            <a className="ov-open" href={link.url} target="_blank" rel="noopener noreferrer nofollow" referrerPolicy="no-referrer">
              Open page
              <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true"><path d="M5 11L11 5M6 5h5v5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The cases, one per site. A new one opens its own room once; the ones
 * already there keep their node, so the list never jumps under her.
 */
export function Cases({
  sites,
  running,
  focus,
  onUnlock,
}: {
  sites: LiveSite[];
  running: boolean;
  /** A site to open and flash, from a toast or the radar. */
  focus: { domain: string; at: number } | null;
  onUnlock: () => void;
}) {
  const shown = sites.filter((site) => !site.locked);
  const locked = sites.filter((site) => site.locked);
  const [open, setOpen] = useState<Set<string>>(() => new Set(shown[0] ? [shown[0].domain] : []));
  const [flash, setFlash] = useState<string | null>(null);
  const known = useRef<Set<string> | null>(null);
  const [entering, setEntering] = useState<Set<string>>(new Set());

  useEffect(() => {
    const now = new Set(shown.map((site) => site.domain));
    if (known.current) {
      const fresh = [...now].filter((domain) => !known.current!.has(domain));
      if (fresh.length) {
        setEntering(new Set(fresh));
        requestAnimationFrame(() => requestAnimationFrame(() => setEntering(new Set())));
      }
    }
    known.current = now;
  }, [shown.map((site) => site.domain).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!focus) return;
    setOpen((current) => new Set(current).add(focus.domain));
    setFlash(focus.domain);
    document.querySelector(`[data-site="${CSS.escape(focus.domain)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setFlash(null), 1400);
    return () => clearTimeout(timer);
  }, [focus]);

  const toggle = (domain: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });

  return (
    <>
      <div className="ov-cases" aria-live="polite">
        {shown.map((site) => (
          <div key={site.domain} className={`ov-slot${entering.has(site.domain) ? ' is-entering' : ''}`}>
            <Case site={site} open={open.has(site.domain)} flash={flash === site.domain} onToggle={() => toggle(site.domain)} />
          </div>
        ))}
      </div>
      {running && locked.length === 0 && (
        <div className="ov-seeking"><i aria-hidden="true" /><span>Looking for more…</span></div>
      )}
      {locked.length > 0 && (
        <div className="ov-locked">
          <div className="ov-lstack">
            {locked.map((site) => (
              <button type="button" className="ov-lrow" key={site.domain} onClick={onUnlock}>
                <span className="ov-lock"><LockIcon /></span>
                <span className="ov-dom">
                  <b>{site.domain}</b>
                  <span className={site.hot ? 'is-hot' : ''}>
                    {site.linkCount} {site.linkCount === 1 ? 'link' : 'links'}
                    {site.match != null ? `, ${site.match}% sure it is you` : ''}
                    {site.inGoogle ? `, ${site.inGoogle} in Google` : ''}
                  </span>
                </span>
                <span className="chip">Locked</span>
              </button>
            ))}
          </div>
          <div className="ov-unlock">
            <b>{locked.length} more {locked.length === 1 ? 'case is' : 'cases are'} locked</b>
            <span>We found them, you just cannot see which sites yet.</span>
            <div className="ov-perks">
              <span>Every site shown</span>
              <span>Notices filed by our team</span>
              <span>Google removal requests</span>
              <span>Removed pages re-checked</span>
            </div>
            <button type="button" className="btn btn-primary" onClick={onUnlock}>Unlock full protection</button>
          </div>
        </div>
      )}
    </>
  );
}

/** Fourteen days of searches as bars: red for new pages, amber for ones back. */
export function Spark({ history }: { history: LiveHistoryRun[] }) {
  const days = lastDays(history);
  const max = Math.max(1, ...days.map((day) => day.found + day.cameBack));
  return (
    <span className="ov-spark" aria-hidden="true">
      {days.map((day) => (
        <i
          key={day.key}
          className={day.cameBack ? 'is-back' : day.found ? 'is-new' : ''}
          style={{ height: `${Math.max(3, ((day.found + day.cameBack) / max) * 28)}px` }}
        />
      ))}
    </span>
  );
}

/** Per calendar day, the last fourteen, oldest first. */
export function lastDays(history: LiveHistoryRun[]) {
  const out: Array<{ key: string; found: number; cameBack: number }> = [];
  for (let back = 13; back >= 0; back--) {
    const day = new Date();
    day.setHours(0, 0, 0, 0);
    day.setDate(day.getDate() - back);
    const key = day.toDateString();
    const runs = history.filter((run) => new Date(run.startedAt).toDateString() === key);
    out.push({ key, found: runs.reduce((n, run) => n + run.found, 0), cameBack: runs.reduce((n, run) => n + run.cameBack, 0) });
  }
  return out;
}
